/***
 * Created on 2023/8/17
 * @author: Adam Cheng <adamcheng@qnap.com>
 ***/

import React, {forwardRef, useEffect, useMemo, useRef, useState} from 'react';
import ReactDom from "react-dom";
import PasswordInput from "../../common/form/password_input";
import {useTranslation} from "react-i18next";
import {useCaptcha} from "../../../hook/captcha";
import {getPrimaryQid} from '../../../service/user'
import {useMutation} from "@tanstack/react-query";
import {apiPost} from "../../../service/api";
import md5 from "blueimp-md5";
import {getErrorMessage} from 'common/ServiceCommon';


const DeleteWarningDialog = forwardRef(function DeleteWarningDialog(props, ref) {
    const {showModal, closeModal, dispatch, ...rest_props} = props;
    const dom = document.querySelector('body .modal-dialog');
    const { t } = useTranslation();
    const reasonList = {
        0: 'I have a duplicate account.',
        1: `I'm getting too many emails and invitation.`,
        2: `I don't feel safe on myQNAPcloud.`,
        3: `I don't understand how to use myQNAPcloud.`,
        4: `This is temporary, I'll be back.`,
        5: 'Other'
    };
    const currentUser = JSON.parse(localStorage.getItem('currentUser'));
    const [qid, setQid] = useState("");
    const [inputValues, setInputValues] = useState({
        password: '',
        reason: ''
    });
    const [otherReason, setOtherReason] = useState("");
    const [passInputValid, setPassInputValid] = useState(false);
    const [valid, setValid] = useState(false);
    const [errorMessage, setErrorMessage] = useState();

    const defaultQID = useMemo(() => {
        if (!currentUser) return "";

        const qid = getPrimaryQid(currentUser);
        setQid(qid);
        return qid;
    }, [qid]);

    const requestRemovalMutate = useMutation({
        mutationFn: async (data) => await apiPost(`/ajax/request_account_removal`, data),
        onError: (error, variables, context) => {
             setErrorMessage(getErrorMessage(t, error.cause?.code));
        }
    })

    const verifyViaEmail = async () => {
        const data = {
            method: 'email',
            password: md5(inputValues.password),
            reason_text: inputValues.reason
        }
        await requestRemovalMutate.mutateAsync(data);
        dispatch({type: 'openEmailSendDialog'})
    }

    const verifyViaPhone = async () => {
         const data = {
            method: 'sms',
            password: md5(inputValues.password),
            reason_text: inputValues.reason
        }
        await requestRemovalMutate.mutateAsync(data);
        dispatch({type: 'openConfirmDeleteDialog', method: 'phone'})
    }

    const onChange = (e, callback, name=null, value=null) => {
        if (callback && typeof callback === 'function') {
            callback(e);
        }
        const _name = name ? name : e.target.name;
        const _value = value ? value: e.target.value;
        setInputValues(prev => {
            return {
                ...prev,
                [_name]: _value
            }
        })
    }

    const {Captcha, hasCaptcha, captchaValue, captchaError} = useCaptcha(onChange);

    useEffect(() => {
        const v = Object.entries(inputValues).filter(([key,value]) => value === '').length === 0;
        const validOther = inputValues.reason === 'Other' ? otherReason !== '' || false : true;
        setValid(v && validOther);

    }, [inputValues.password, inputValues.reason, otherReason, captchaValue])

    const sectionOne = (
        <div className="dialog-content">
            <div className="dialog__sub-title color-primary">
                {t('@Delete the QID account@')}
                <span className="display-inline-block pl-4 pr-4">{defaultQID}</span>
            </div>
            <div className="dialog__row-divide mt-20 mb-20"></div>

            <PasswordInput
                label={t('@Password@')}
                placeholder={t('@Password@')}
                value={inputValues.password}
                name="password"
                onChange={onChange}
                status={setPassInputValid}
                compType="dialog"
                required={true}/>

            {hasCaptcha && Captcha }

            { captchaError && <p className="text-error">{captchaError}</p>}

            {errorMessage && <p className="text-error">{errorMessage}</p>}

            <div className="dialog__form-group">
                <label className="required">{t('@Reason for leaving@')}</label>
                <div className="dialog__info-grid">
                    {
                        Object.keys(reasonList).map((key) => (
                            <div className={`info-grid__row pt-8 pr-16 pb-8 pl-16 ${key==='5' ? '!flex-wrap-wrap' : ''}`} key={key}>
                                <div className="comp-radio">
                                    <label className="q-radio" htmlFor={`r${key}`}>
                                        <input type="radio" name="reason" id={`r${key}`} value={reasonList[key]} onChange={onChange}/>
                                        {t(`@${reasonList[key]}@`)}
                                    </label>
                                </div>
                                {key==='5' && <textarea className="display-block w-full h-80px mt-10 mb-10" value={otherReason} onChange={(e)=>setOtherReason(e.target.value)}></textarea>}
                            </div>
                        ))
                    }
                </div>
            </div>
        </div>
    );

    const modal = (
        <div className="comp-dialog-wrapper">
            <div className="comp-dialog" data-modal-dialog>
                <div className="dialog-header">
                    <div className="dialog-header__title">{t('@Permanently Delete Account@')}</div>
                    <div className="dialog-header__btn-close cursor-pointer" data-close-dialog onClick={closeModal}></div>
                </div>
                <div className="dialog-body">
                    {sectionOne}
                </div>
                <div className="dialog-footer">
                    <div className="q-btn-group btn-align-center">
                        {!defaultQID?.match(/^\d+$/) && <button className="q-btn q-btn--primary" disabled={!valid} onClick={async ()=> await verifyViaEmail()}>{t('@Verify via Email@')}</button>}
                        {defaultQID?.match(/^\d+$/) && <button className="q-btn q-btn--primary" disabled={!valid} onClick={verifyViaPhone}>{t('@Verify via SMS@')}</button>}
                    </div>
                </div>
            </div>
        </div>
    );

    return ReactDom.createPortal(props.showModal && modal, dom);
});

export default DeleteWarningDialog;